import { useContext, useEffect, useState } from "react";
import { Modal, Row, Col, Button, Alert, Form } from "react-bootstrap";

import AddCustomFields from "./AddCustomFields";
import RetainerAgreement from "./RetainerUploadComponent";
import ConsentFormStep from "./ConsentFormStep";
import { ActivityCenterContext } from "../../../../../Context/ActivityCenter";
import {
  CustomField,
  DocumentRequestsModel,
  RetainerState,
  ConsentFormState,
  DocumentSavingPreferenceType,
  ConsentFormProgress,
  ClientPortalPage,
} from "../../../types";
import StateContext from "../../../context/State";
import { DefaultDocumentRequests, defaultRetainerState, defaultConsentFormState } from "../../../data";
import PortalService from "../../../services/portal.service";
import getArrayBufferFromFile from "../../../../../util/getArrayBufferFromFile";
import { runInnerFunction } from "../../../../../compatManager";
import { useAppSelector } from "../../../../../redux/hooks";
import { lddStateObject } from "../../../../../redux/reducer";
import { RPWDataReader, getDataReader, getDocumentSavingPreference } from "../../../../../livedataFuncs";
import { prepareConsentFormDocument } from "../../../utils/consentFormWorkflow";
import { validateRetainer, validateConsentForm } from "../../../utils/documentValidation";

import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import AddCustomDocuments from "./AddCustomDocuments";
import { getCustomDocumentSet } from "../../../../Settings/ClientConnectFirmSettings/CustomDocumentSets/customDocumentSetFuncs";
import { listFirmRetainerFiles } from "../../../../Settings/ClientConnectFirmSettings/ClientWrittenRetainerCard/firmRetainerFuncs";

export type NewClientPortalProps = {
  showModal: boolean;
  onHideModal: () => void;
  selectedOptions: {
    RequestIDNumber: '1' | '2';
    clientConnectId: boolean;
    addIntakeFields: boolean;
    retainerAgreement: boolean;
    consentForm: boolean;
    documentsRequested: boolean;
    sourceOfFunds: boolean;
    remoteClosingCalls: boolean;
    requireFaceImage: boolean;
  };
  onRequestIDNumberChange?: (value: '1' | '2') => void;
};

const NewClientPortal = (props: NewClientPortalProps) => {
  const { state, setState } = useContext(StateContext);
  const portalService = new PortalService(state);
  const activityCenter = useContext(ActivityCenterContext);
  
  // Retainer state
  const [retainerState, setRetainerState] = useState<RetainerState>(defaultRetainerState);
  const [uploadedRetainerFile, setUploadedRetainerFile] = useState<File | null>(null);
  const [firmRetainerSourceName, setFirmRetainerSourceName] = useState<string | null>(null);
  
  // Consent form state
  const [consentFormState, setConsentFormState] = useState<ConsentFormState>(defaultConsentFormState);
  const [uploadedConsentFile, setUploadedConsentFile] = useState<File | null>(null);
  
  const [customFieldsState, setCustomFieldsState] = useState({
    fields: [] as CustomField[],
    fieldSetNames: [] as string[],
    documentSetNames: [] as string[],
    documentRequests: DefaultDocumentRequests as DocumentRequestsModel
  });

  const [wizardState, setWizardState] = useState({
    currentStep: 0,
    language: "EN" as string,
    replyTo: 1 as number,
    isLoading: false
  });
  
  // Wizard state helper functions
  const goToNextStep = () => setWizardState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  const resetWizard = () => setWizardState(prev => ({ ...prev, currentStep: 0 }));
  const setLanguage = (language: string) => setWizardState(prev => ({ ...prev, language }));
  const setReplyTo = (replyTo: number) => setWizardState(prev => ({ ...prev, replyTo }));
  const setWizardLoading = (isLoading: boolean) => setWizardState(prev => ({ ...prev, isLoading }));
  
  const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
  let DataReaderObj: RPWDataReader;
  DataReaderObj = getDataReader(livedataRootContainer.dataRoot);
  
  const sid = useAppSelector((state) => state.SessionInfo.sid);
  const appProvinceShort = useAppSelector((state) => state.SessionInfo.appProvinceShort);
  const [firmRetainerFileNames, setFirmRetainerFileNames] = useState<string[]>([]);

  useEffect(() => {
    if (!props.showModal || !props.selectedOptions.retainerAgreement || !sid || !appProvinceShort) {
      return;
    }
    let cancelled = false;
    void listFirmRetainerFiles(sid, appProvinceShort)
      .then((files) => {
        if (!cancelled) {
          setFirmRetainerFileNames(files);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFirmRetainerFileNames([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props.showModal, props.selectedOptions.retainerAgreement, sid, appProvinceShort]);
  
  const documentSavingPreference: DocumentSavingPreferenceType = getDocumentSavingPreference(livedataRootContainer?.dataRoot);

  const langMap = {
    "English": "EN",
    "Chinese": "zhCN",
    "French": "FR"
  };

  const reverseLangMap = {
    "EN": "English",
    "zhCN": "Chinese",
    "FR": "French"
  };

  const getIncludeFirmEmail = async (sid: string): Promise<boolean> => {
    try {
        const res = await fetch(`getprofile.asp?pname=IncludeFirmEmail&SessionID=${ sid }`);
        const textData = await res.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(textData, 'text/xml');
        const includeEmailValue = xmlDoc.getElementsByTagName('IncludeEmail')[0]?.childNodes[0]?.nodeValue;
        if (includeEmailValue !== undefined) {
            return includeEmailValue === '1';
        } else {
            return false;
        }
    } catch (e) {
        console.log('Unable to get Include Firm Email setting: ', e);
        return false;
    }
};

  const createPortal = async (): Promise<void> => {
    setWizardLoading(true);
    setState({ ...state, page: ClientPortalPage.LOADING });

    if (!state.appId) {
      alert("No file!");
      setState({ ...state, page: ClientPortalPage.INITIATE });
      return;
    }

    let portalId: number | null = null;
    let cleanupAttempted = false;

    const performCleanup = async (reason: string): Promise<void> => {
      if (cleanupAttempted || !portalId) {
        return; // Already cleaned up or no portal to clean
      }
      cleanupAttempted = true;
      try {
        await portalService.deletePortal(portalId);
        console.log(`Cleaned up orphaned portal ${portalId} - Reason: ${reason}`);
      } catch (deleteError) {
        console.error(`Failed to cleanup orphaned portal ${portalId}:`, deleteError);
        // Still mark as attempted to prevent infinite loops
      }
    };

    try {
      const preparedConsentBlob = await prepareConsentDocumentsPhase();
      
      portalId = await createPortalPhase();
      
      await updateDashboardPhase(portalId);
      
      const uploadSuccess = await uploadDocumentsPhase(portalId, preparedConsentBlob);
      if (!uploadSuccess) {
        await performCleanup('upload failure');
        return;
      }
      
      // Try to save configurations - if this fails, we need to cleanup
      try {
        await saveConfigurationsPhase(portalId);
      } catch (configError) {
        console.error('Failed to save portal configurations:', configError);
        await performCleanup('configuration save failure');
        throw configError; // Re-throw to trigger outer catch block for user notification
      }
      
      await sendNotificationsPhase(portalId);
      
      setWizardLoading(false);
      props.onHideModal();
      
    } catch (e) {
      console.error('Portal creation failed:', e);
      
      // Cleanup: Delete the orphaned portal if it was created
      // (This is a safety net in case cleanup didn't happen above)
      await performCleanup('creation failure (safety net)');
      
      setWizardLoading(false);
      setState({ ...state, page: ClientPortalPage.INITIATE, progressMessage: undefined });
      alert(`Unable to create Client Connect portal.`);
    }
  };

  const setCustomFieldsData = async (portalId) => {
    await portalService.setClientPortalCustomFields(
      portalId,
      customFieldsState.fields,
      "beforeInit"
    );
  };

  const prepareConsentDocumentsPhase = async (): Promise<Blob | null> => {
    if (!props.selectedOptions.consentForm || !consentFormState.defaultConsentFormRequired) {
      return null;
    }

    try {
      return await prepareConsentFormDocument({
        appId: state.appId,
        activityCenter,
        DataReaderObj,
        onProgressUpdate: (message) => {
          setState({ ...state, page: ClientPortalPage.LOADING, progressMessage: message });
        }
      });
    } catch (error) {
      console.error('Error preparing consent form:', error);
      alert(`Failed to prepare consent form: ${error.message}`);
      setWizardLoading(false);
      setState({ ...state, page: ClientPortalPage.INITIATE, progressMessage: undefined });
      throw error;
    }
  };

  const createPortalPhase = async (): Promise<number> => {
    const IncludeEmail = await getIncludeFirmEmail(sid);
    DataReaderObj.setReplyTo(wizardState.replyTo);
    
    const spouseInfo : { SpouseName: string | null, SpouseEmail: string | null }[] = DataReaderObj.getSpouseNotOnTitleInfo();
    
    const extraContent = {
      includeEmail: IncludeEmail,
      replyToFirmContact: wizardState.replyTo,
      spouseEmailObject: spouseInfo
    };
    
    return await portalService.createPortal(wizardState.language, extraContent, props.selectedOptions.RequestIDNumber as string);
  };

  const updateDashboardPhase = async (portalId: number): Promise<void> => {
    try {
      await runInnerFunction("ReactShell.ClientConnectDashboardUpdate", {
        selectedOptions: props.selectedOptions,
        portalId: portalId
      });
    } catch (e) {
      alert(`Error in updating dashboard: ${e.message || e}`);
    }
  };

  const uploadDocumentsPhase = async (
    portalId: number,
    preparedConsentBlob: Blob | null
  ): Promise<boolean> => {
    try {
      const documentTemplates = await getCustomDocumentSet(sid);
      const selectedTemplateFields = documentTemplates
        .filter(template => customFieldsState.documentSetNames.includes(template.fieldSetName))
        .flatMap(template => template.fields.map(field => field.name));

      const updatedDocumentRequests = [
        ...customFieldsState.documentRequests.filter(d => d.documentName),
        ...selectedTemplateFields.map(fieldName => ({ documentName: fieldName }))
      ];
      
      await runInnerFunction("ClientPortalUpdateAndSaveFile", {
        initClientConnect: true,
        documentRequest: updatedDocumentRequests,
        clientConnectId: portalId,
        customFieldSets: customFieldsState.fieldSetNames,
        language: wizardState.language
      });
      
      if (props.selectedOptions.retainerAgreement && uploadedRetainerFile && retainerState.uploadRetainer) {
        const success = await safeUpload(
          () => uploadFileToPortal(uploadedRetainerFile, portalId, "retainer.pdf", "Retainer"),
          "retainer"
        );
        if (!success) return false;
      }
      
      if (props.selectedOptions.consentForm && preparedConsentBlob) {
        const success = await safeUpload(
          () => uploadFileToPortal(preparedConsentBlob, portalId, "consent.pdf", "Consent"),
          "prepared consent form"
        );
        if (!success) return false;
      }
      
      if (props.selectedOptions.consentForm && uploadedConsentFile && consentFormState.uploadConsentForm) {
        const success = await safeUpload(
          () => uploadFileToPortal(uploadedConsentFile, portalId, "consent.pdf", "Consent"),
          "uploaded consent form"
        );
        if (!success) return false;
      }
      
      return true;
    } catch (e) {
      alert(e);
      return false;
    }
  };

  const saveConfigurationsPhase = async (portalId: number): Promise<void> => {
    setState({ ...state, progressMessage: ConsentFormProgress.CREATING_PORTAL });
    
    await setRetainerData(portalId);
    await setConsentFormData(portalId);
    await setCustomFieldsData(portalId);
  };

  const isValidEmail = (email: string): boolean => {
    if (!email || email.trim() === '') {
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const sendNotificationsPhase = async (portalId: number): Promise<void> => {
    setState({ ...state, progressMessage: ConsentFormProgress.NOTIFYING_CLIENTS });
    
    const province = await portalService.getProvince();
    const emailInfos: string[] = DataReaderObj.getClientContactInfo(province);
    
    for (const email of emailInfos) {
      if (!isValidEmail(email)) {
        console.warn(`Skipping invalid email format: ${email}`);
        continue;
      }

      const emailData = {
        appId: state.appId,
        from: 'noreply@ldd.ca',
        to: email,
        subject: 'ClientConnect code',
        message: 'Your Lawyer has added you to ClientConnect. Please log in',
        ccEmail: '',
        bccEmail: '',
        user: ''
      };

      try {
        await portalService.logEmail(state.appId, emailData);
      } catch (error) {
        console.error(`Failed to notify client via email at ${email}`, error);
      }
    }
    
    setState({ ...state, page: ClientPortalPage.LOADING, progressMessage: ConsentFormProgress.FINALIZING });
  };

  const handleUploadFailure = (documentType: string, error: unknown): void => {
    console.error(`Failed to upload ${documentType}:`, error);
    setWizardLoading(false);
    setState({ ...state, page: ClientPortalPage.INITIATE });
  };

  const safeUpload = async (
    uploadFn: () => Promise<void>,
    documentType: string
  ): Promise<boolean> => {
    try {
      await uploadFn();
      return true;
    } catch (error) {
      handleUploadFailure(documentType, error);
      return false;
    }
  };

  /**
   * Upload a user-selected file (retainer or consent form) to Azure storage
   * @param fileOrBlob - The file or blob to upload
   * @param targetPortalId - The portal ID to upload to
   * @param fileName - The name to save the file as
   * @param folderName - The folder to upload to (e.g., "Retainer", "Consent")
   */
  const uploadFileToPortal = async (
    fileOrBlob: File | Blob,
    targetPortalId: number | string,
    fileName: string,
    folderName: string,
  ): Promise<void> => {

    const arrayBuffer = fileOrBlob instanceof File ? await getArrayBufferFromFile(fileOrBlob) : await fileOrBlob.arrayBuffer();
    await portalService.uploadDocument(arrayBuffer, targetPortalId.toString(), state.appId, fileName, folderName);
  };

  /**
   * Save retainer configuration to database (called AFTER portal creation)
   * Matches the pattern of setConsentFormData
   * Note: This ONLY saves to database, file upload is handled separately
   */
  const setRetainerData = async (portalId: number): Promise<void> => {
    try {
      if (props.selectedOptions.retainerAgreement) {
        await portalService.setRetainerType(retainerState, state.appId, portalId);
      }
    } catch (e) {
      alert(`Error setting retainer data: ${e}`);
      throw e; // Re-throw to allow parent to handle
    }
  };

  const setConsentFormData = async (portalId: number): Promise<void> => {
    try {
      if (props.selectedOptions.consentForm) {
        await portalService.setConsentFormType(consentFormState, state.appId, portalId);
      }
    } catch (e) {
      console.error('Error setting consent form data:', e);
      alert(`Error setting consent form data: ${e}`);
      throw e; // Re-throw to allow parent to handle
    }
  };

  const steps = [
    "ClientConnectID",
    "Written Retainer",
    "Information Sharing Consent Form",
    "Intake Templates",
    "Document Requests",
    "Summary",
  ];


  // Mapping step labels to `selectedOptions` keys
  const stepOptionsMap = {
    "ClientConnectID": "clientConnectId",
    "Written Retainer": "retainerAgreement",
    "Information Sharing Consent Form": "consentForm",
    "Intake Templates": "addIntakeFields",
    "Document Requests": "documentsRequested",
    "Summary": "",
  };

  // Filtering steps based on selected options
  const filteredSteps = steps.filter(
    (step) =>
      stepOptionsMap[step] === "" || props.selectedOptions[stepOptionsMap[step]]
  );

  /**
   * Handle "Next" button click with step-level validation
   * Validates the current step before allowing user to proceed
   */
  const handleNextClick = () => {
    const currentStep = filteredSteps[wizardState.currentStep];
    
    // Validate retainer step before advancing
    if (currentStep === "Written Retainer" && props.selectedOptions.retainerAgreement) {
      const validation = validateRetainer(retainerState, uploadedRetainerFile);
      if (!validation.isValid) {
        alert(validation.error);
        return; // Don't advance if validation fails
      }
    }
    
    // Validate consent form step before advancing
    if (currentStep === "Information Sharing Consent Form" && props.selectedOptions.consentForm) {
      const validation = validateConsentForm(consentFormState, uploadedConsentFile);
      if (!validation.isValid) {
        alert(validation.error);
        return; // Don't advance if validation fails
      }
    }
    
    // All validations passed, advance to next step
    goToNextStep();
  };

  return (
    <Modal
      show={props.showModal}
      onHide={props.onHideModal}
      size="lg"
      className="initiate-cc"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          Initiate ClientConnect
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row>
          <Col>
            <Box sx={{ width: "100%" }}>
              <Stepper activeStep={wizardState.currentStep} alternativeLabel>
                {filteredSteps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Col>
        </Row>
        <br />
        <Row>
        {filteredSteps[wizardState.currentStep] === "ClientConnectID" && (
            <Col>
              <h3>Client Connect ID Verification</h3>
              <Alert>
                Thank you for picking ClientConnect ID Verification!
              </Alert>
              <Form>
                <div className="mb-3">
                  <Form.Check
                    type="radio"
                    id="request-1-id"
                    name="idVerificationOptions"
                    label="Request 1 piece of ID per-client"
                    checked={props.selectedOptions.RequestIDNumber === '1'}
                    onChange={() => props.onRequestIDNumberChange?.('1')}
                  />
                  <Form.Check
                    type="radio"
                    id="request-2-id"
                    name="idVerificationOptions"
                    label="Request 2 pieces of ID per-client"
                    checked={props.selectedOptions.RequestIDNumber === '2'}
                    onChange={() => props.onRequestIDNumberChange?.('2')}
                  />
                </div>
              </Form>
            </Col>
          )}
          {filteredSteps[wizardState.currentStep] === "Written Retainer" && (
            <Col>
              <RetainerAgreement
                setRetainerState={setRetainerState}
                retainerState={retainerState}
                setUploadedFile={setUploadedRetainerFile}
                uploadedRetainerFile={uploadedRetainerFile}
                firmRetainerSourceName={firmRetainerSourceName}
                setFirmRetainerSourceName={setFirmRetainerSourceName}
                firmRetainerFileNames={firmRetainerFileNames}
                firmRetainerSessionId={sid}
                firmRetainerProvince={appProvinceShort}
              />
            </Col>
          )}
          {filteredSteps[wizardState.currentStep] === "Information Sharing Consent Form" && (
            <Col>
              <ConsentFormStep
                consentFormState={consentFormState}
                setConsentFormState={setConsentFormState}
                setUploadedConsentFile={setUploadedConsentFile}
                documentSavingPreference={documentSavingPreference}
              />
            </Col>
          )}
          {filteredSteps[wizardState.currentStep] === "Intake Templates" && (
            <Col>
              <AddCustomFields
                setCustomFields={(fields) => setCustomFieldsState(prev => ({ ...prev, fields }))}
                setCustomFieldSetNames={(names) => setCustomFieldsState(prev => ({ ...prev, fieldSetNames: names }))}
                customFieldSetNames={customFieldsState.fieldSetNames}
                afterInit={false}
              />
            </Col>
          )}
          {filteredSteps[wizardState.currentStep] === "Document Requests" && (
            <Col>
              <AddCustomDocuments
                setCustomFieldSetNames={(names) => setCustomFieldsState(prev => ({ ...prev, documentSetNames: names }))}
                setDocumentRequests={(requests) => setCustomFieldsState(prev => ({ ...prev, documentRequests: requests }))}
                customFieldSetNames={customFieldsState.documentSetNames}
                afterInit={false}
              />
            </Col>
          )}
          {filteredSteps[wizardState.currentStep] === "Summary" && (filteredSteps.length > 1) && (
            <Col>
            <Alert>
              Initiating ClientConnect will invite your clients to submit
              their information via their primary contact email or phone
              number.
            </Alert>
            <Form>
              <Form.Group controlId="languageSelect">
                <Form.Label>Language</Form.Label>
                <Form.Control
                  as="select"
                  value={reverseLangMap[wizardState.language]}
                  onChange={(e) => {
                    setLanguage(langMap[e.target.value]);
                  }}
                >
                  <option value="English">English</option>
                  <option value="Chinese">Chinese</option>
                  <option value="French">French</option>
                </Form.Control>
              </Form.Group>
              <Form.Group controlId="replyToSelect">
                <Form.Label>Reply To</Form.Label>
                <Form.Control
                  as="select"
                  onChange={(e) => {
                    setReplyTo(e.target.value === "Firm Contact" ? 1 : 0);
                  }}
                  style={{cursor: 'pointer'}}
                >
                  <option value="Firm Contact">Firm Contact</option>
                  <option value="Responsible Lawyer">Responsible Lawyer</option>
                </Form.Control>
              </Form.Group>
            </Form>
          </Col>
          )}
          {filteredSteps[wizardState.currentStep] === "Summary" && (filteredSteps.length === 1) && (
            <Col>
              <Alert>
                Please select some intake fields. Thank you!
              </Alert>
            </Col>
          )}

        </Row>
      </Modal.Body>
      <Modal.Footer>
        {wizardState.currentStep > 0 && (
          <Button variant="outline-primary" onClick={resetWizard}>
            Start Over
          </Button>
        )}

        {/* Adjusting the Skip Button: Only show it if there's more than one step left. */}
        {wizardState.currentStep < filteredSteps.length - 1 && (
          <Button
            variant="outline-primary"
            onClick={goToNextStep}
          >
            Skip
          </Button>
        )}

        {/* Adjusting the Next Button: Show it if it's not on the last step. */}
        {wizardState.currentStep < filteredSteps.length - 1 && (
          <Button variant="primary" onClick={handleNextClick}>
            Next
          </Button>
        )}

        {/* Adjusting the Initiate Button: Show it only on the last step. */}
        {wizardState.currentStep === filteredSteps.length - 1 && (
          <Button
            variant="primary"
            onClick={async () => {
              await createPortal();
              window.location.reload();
            }}
            disabled={wizardState.isLoading || filteredSteps.length === 1}
          >
            Initiate
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
  
};

export default NewClientPortal;
